import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
} from '@mui/material';
import { Print, Download } from '@mui/icons-material';
import { Sale, Salon } from '../types';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ReceiptProps {
  sale: Sale;
  salon: Salon;
  onClose: () => void;
}

export default function Receipt({ sale, salon, onClose }: ReceiptProps) {
  const receiptRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePrint = () => {
    const printContent = receiptRef.current;
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Receipt - ${sale.id}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .receipt { max-width: 400px; margin: 0 auto; }
                .header { text-align: center; margin-bottom: 20px; }
                .business-name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
                .business-info { font-size: 14px; margin-bottom: 5px; }
                .divider { border-top: 2px solid #000; margin: 20px 0; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
                .total { font-weight: bold; font-size: 18px; }
                .footer { text-align: center; margin-top: 30px; font-size: 12px; }
              </style>
            </head>
            <body>
              <div class="receipt">
                ${printContent.innerHTML}
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const handleDownload = async () => {
    const receiptElement = receiptRef.current;
    if (!receiptElement) return;

    try {
      // Create canvas from the receipt element
      const canvas = await html2canvas(receiptElement, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });

      // Calculate dimensions for PDF
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position = 0;

      // Add the image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `receipt-${sale.id.slice(-8).toUpperCase()}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to download receipt. Please try printing instead.');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Receipt</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            sx={{ mr: 1 }}
            color="primary"
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            Print
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>

      <Paper ref={receiptRef} sx={{ p: 4, maxWidth: 600, mx: 'auto', boxShadow: 3 }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{
              mb: 3,
              color: 'primary.main',
              textTransform: 'uppercase',
              letterSpacing: 1
            }}
          >
            {salon.name}
          </Typography>

          {/* Address Block */}
          <Box sx={{ mb: 3, px: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.9rem',
                textAlign: 'center'
              }}
            >
              {salon.address?.split(',').map((line, index) => (
                <span key={index}>
                  {line.trim()}
                  {index < salon.address!.split(',').length - 1 && <br />}
                </span>
              ))}
            </Typography>
          </Box>

          {/* Phone */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.1rem'
            }}
          >
            📞 {salon.phone}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Receipt Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, px: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Receipt No:
            </Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
              #{sale.id.slice(-8).toUpperCase()}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Date:
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatDate(sale.createdAt)}
            </Typography>
          </Box>
        </Box>

        {/* Payment Method */}
        <Box sx={{ mb: 3, px: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Payment Method:
          </Typography>
          <Typography variant="body1" fontWeight="medium" sx={{
            textTransform: 'capitalize',
            bgcolor: 'grey.100',
            px: 2,
            py: 0.5,
            borderRadius: 1,
            display: 'inline-block'
          }}>
            💳 {sale.paymentMethod}
          </Typography>
        </Box>

        {sale.notes && (
          <Box sx={{ mb: 3, px: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Notes:
            </Typography>
            <Typography variant="body1" sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 1,
              fontStyle: 'italic'
            }}>
              {sale.notes}
            </Typography>
          </Box>
        )}

        {/* Items Table */}
        <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Item
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Price
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sale.items.map((item, index) => (
                <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 500 }}>{item.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(item.totalPrice)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total Section */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          p: 2,
          borderRadius: 1,
          mb: 3
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            TOTAL AMOUNT:
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
            {formatCurrency(sale.totalAmount)}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Footer */}
        <Box textAlign="center" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
              mb: 1
            }}
          >
            Thank you for your business! ✨
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            We appreciate your visit to {salon.name}
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            "Beauty is not in the face, beauty is a light in the heart"
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
